<template>

  <LoadingDiv v-if="!isReady" />

  <div v-else>
    <div v-if="encounter">
      <div class="section">
        <div class="card z-depth-0 grey lighten-4">
          <br />
          <span class="new badge left" data-badge-caption="Scheme">{{
            encounter.scheme.name.toUpperCase()
          }}</span>

          <!-- Encounter Bio -->
          <div class="card-content">
            <div class="row">
              <div class="col s8">
                <p>{{ encounter.client_name }}</p>
                <h5>
                  {{ encounter.name }}
                  <sup><small><span class="black-text">{{
                    showAgeSex(encounter?.dob, encounter?.sex)
                        }}</span></small></sup>
                  &nbsp;
                  <a href="#" class="orange-text"><i class="material-icons teal lighten-4" title="clear"
                      @click.prevent="handleRestart()">clear</i></a>
                </h5>
                <h6>({{ encounter.enrollee_number }})</h6>
                <span v-if="encounterDate || encounterRegister">
                  <p>
                    <br />
                    <span class="chip black white-text">
                      {{
                        new Date(encounterDate ? encounterDate : encounterRegister).toLocaleString(undefined, {
                          weekday: "long",
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })
                      }}
                    </span>
                    <i class="tiny material-icons red-text clickable" title="Reset date" @click="handleDateReset()"
                      v-if="!encounter?.encounter_date">cancel</i>
                  </p>
                </span>
              </div>
              <div class="col s4">
                <img :src="encounter.profile_img" width="128" class="circle responsive-img right"
                  v-if="encounter.profile_img" />
                <img :src="`https://ui-avatars.com/api/?name=${encounter.name}&size=128`" width="128"
                  class="circle responsive-img right" v-else />
              </div>
            </div>
          </div>

          <!-- Encounter Starts: Date Form -->
          <template v-if="!encounterDate || !encounterRegister">
            <div class="card-panel teal lighten-5" :class="{ 'red lighten-5': error }" v-if="!encounterRegister">
              <div class="row">
                <div class="col s12">
                  <span class="card-title">Set Encounter</span>
                </div>
                <div class="col s12">
                  <div class="input-field">
                    <input type="text" v-model="encounterDate" class="datepicker validate" required />
                    <label class="active teal-text" for="encounterDate">Day of Visit</label>
                  </div>
                </div>
              </div>
            </div>
            <div class="card teal lighten-5" v-else>
            <div class="card-panel teal lighten-5" :class="{ 'red lighten-5': error }">
              <div class="row">
                <a class="waves-effect waves-light btn btn-large red lighten-2 col s12 m2"
                  :class="{ disabled: isSubmitting }" @click.prevent="handleRestart()">
                  <i class="medium material-icons left">cancel</i>Cancel
                </a>
                <span class="col m5"></span>
                <a class="waves-effect waves-light btn btn-large teal darken-2 col s12 m3"
                  :class="{ disabled: isSubmitting }" @click.prevent="handleEncounterNext('save')">
                  <i class="large material-icons left">add_circle_outline</i> Save Visit
                </a>
                <a class="waves-effect waves-light btn btn-large black lighten-2 col s12 m2"
                  :class="{ disabled: isSubmitting }" @click.prevent="handleEncounterNext('auth')">
                  <i class="large material-icons left">chevron_right</i> Auth
                </a>
              </div>
            </div>
            </div>
            <!-- Sumbmitting -->
            <div class="progress" v-if="isSubmitting">
              <div class="indeterminate"></div>
            </div>
          </template>

          <!-- Encounter Details -->
          <template v-else>
            <!-- Check for active encoounter -->
            <div class="progress" v-if="isLoading">
              <div class="indeterminate"></div>
            </div>
            <!-- Entire Provider (diagnosis, claims) Display starts here -->
            <div class="card grey lighten-4" v-else>

              <div id="claim"></div>

              <!-- Display Diagnosis -->
              <div class="row teal lighten-5" v-if="diagnosisList.length" id="claim">
                <div class="col s12">
                  <div class="card-content yellow lighten-1">
                    <span class="black-text text-lighten-5">
                      <p>
                        <i class="material-icons black-text">bookmark_border</i>
                        <span v-if="encounter?.diagnosis">
                          Diagnosis: {{ encounter?.diagnosis === diagnosisList.join(", ") ? diagnosisList.join(", ") :
                            `(${encounter?.diagnosis}) ${diagnosisList.join(", ")}` }}
                        </span>
                        <span v-else>
                          Diagnosis: {{ diagnosisList.join(", ") }}
                        </span>
                        <a href="#" class="secondary-content edit-black-item right"><i class="material-icons" :class="{
                          'black-text': !resetDiagnosis,
                          'blue-text': resetDiagnosis,
                        }" title="edit diagnosis" @click.prevent="handleReviewDiagnosis">edit</i></a>
                      </p>
                    </span>
                  </div>
                </div>
              </div>

              <!-- Claims Display Card -->
              <div class="card" v-if="claimsDisplayCard">
                <div class="card-content">
                  <span class="card-title">Item(s)</span>

                  <!-- List claims -->
                  <ol id="item-list" class="collection">
                    <li class="collection-item" v-for="claim in claims" :key="claim.id">
                      <b>{{ claim.claim }}</b> x {{ claim.qty }} (₦{{
                        claim.amt
                          .toString()
                          .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      }}) <span class="chip blue lighten-5 pointable" v-if="claim.amt == 0"
                        title="capitated item">Cap</span>

                      <a href="#" class="secondary-content cancel-item" @click.prevent="handleDelete(claim)"
                        title="delete"><i class="material-icons grey-text">cancel</i></a>

                      <a href="#" class="secondary-content edit-item" @click.prevent="handleEdit(claim)" title="edit"><i
                          class="material-icons" :class="{
                            'grey-text': editId != claim.id,
                            'blue-text': editId == claim.id,
                          }">edit</i></a>
                    </li>
                  </ol>

                  <!-- Claims Submit -->
                  <div v-if="claimsSubmitCard">
                    <!-- Total -->
                    <ul id="item-list" class="collection">
                      <li class="collection-item pull-right">
                        <b>Total</b> (Sent Amount):
                        <b><span class="red-text">₦{{ totalSent(claimsList) }}</span></b>
                      </li>
                    </ul>

                    <div id="edit"></div>

                    <br />

                    <!-- Add -->
                    <div class="row">
                      <button class="waves-effect waves-light btn-large black col s12 m2 left"
                        :class="{ disabled: isSubmitting }" @click.prevent="handleNext('moreClaims')"><i
                          class="medium material-icons left">add</i>Add</button>
                      <!-- Breaker -->
                      <span class="co1 m7"></span>
                      <!-- Submit -->
                      <button class="waves-effect waves-light btn-large green col s12 m3 right modal-trigger" :class="{
                        disabled: isSubmitting,
                      }" data-target="confirmSubmit"><i class="large material-icons left">send</i>{{
                      isSubmitting ? "Submitting ..." : "Submit" }}
                      </button>
                    </div>

                    <!-- Confirmation -->
                    <Modal id="confirmSubmit" class="modal" :title="alertTitle" :message="alertMessage"
                      @confirm="handleConfirm" />
                  </div>
                </div>
              </div>

              <!-- Sumbmitting -->
              <div class="progress" v-if="isSubmitting">
                <div class="indeterminate"></div>
              </div>

              <!-- Add claim -->
              <!-- <AddClaim :enrollee="encounter" :type="type" :next="next" :reload="reload" :editDiagnosis="resetDiagnosis"
                :claimItem="claim" :level="level" @update="handleUpdate" @clear="handleClear" /> -->

            <AddClaim :turl="{ sid: encounter?.scheme_id, pid: encounter?.provider_id }" :type="type" :next="next" :reload="reload" :editDiagnosis="resetDiagnosis" :claimItem="claim"
                @update="handleUpdate" @clear="handleClear" />

              <!-- Approvals -->
              <div class="card" v-if="encounter?.claims?.length">
                <ol class="collection">
                  <span v-for="claim in encounter.claims.slice().reverse()" :key="claim.id">

                    <!-- approved -->
                    <span v-if="claim.authorization == 'y'">
                      <li class="collection-item"
                        :class="{ 'grey lighten-2': claim.sent_price == 0, 'green lighten-4': claim.sent_price != 0 }"
                        v-if="claim.sent_price === claim.approved_price">
                        <b>{{ claim.item }}</b> x {{ claim.sent_qty }}
                        ({{
                          toMoney(claim.sent_price)
                        }}) <span v-if="claim.sent_price != 0">-> (Approved)</span> ->
                        <span class="blue-text tooltipped pointable" data-position="top" :data-tooltip="claim.comment"
                          v-if="claim.comment">({{
                            claim.authorization_code }})</span>
                        <span class="black-text" v-else>({{ claim.authorization_code }})</span>

                        <span class="secondary-content pointable"
                          :class="{ 'black-text': claim.sent_price == 0, 'green-text': claim.sent_price != 0 }"
                          :title="claim.sent_price == 0 ? 'Completed' : 'Approved'">
                          <i class="material-icons hide-on-small-only">check_circle</i>
                          <i class="tiny material-icons hide-on-med-and-up">check_circle</i>
                        </span>
                      </li>
                      <li class="collection-item blue lighten-4" v-else>
                        <b>{{ claim.item }}</b> x {{ claim.sent_qty }} (Charged:
                        {{
                          toMoney(claim.sent_price)
                        }}) -> x {{ claim.approved_qty }} (Approved: <b>{{ toMoney(claim.approved_price) }}</b>) ->
                        <span class="blue-text text-darken-4 tooltipped pointable" data-position="top"
                          :data-tooltip="claim.comment" v-if="claim.comment">({{
                            claim.authorization_code }})</span>
                        <span class="black-text" v-else>({{ claim.authorization_code }})</span>

                        <span class="secondary-content green-text pointable" title="Approved">
                          <i class="material-icons hide-on-small-only">check_circle</i>
                          <i class="tiny material-icons hide-on-med-and-up">check_circle</i>
                        </span>
                      </li>
                    </span>

                    <!-- pending -->
                    <li class="collection-item lime lighten-4 pointable"
                      v-if="claim.authorization == 'z' && claim.status == 'a'" title="pending request">
                      <b>{{ claim.item }}</b> x {{ claim.sent_qty }} (Charged:
                      {{
                        toMoney(claim.sent_price)
                      }})

                      <span class="secondary-content orange-text pointable" title="Pending">
                        <i class="material-icons hide-on-small-only">watch_later</i>
                        <i class="tiny material-icons hide-on-med-and-up">watch_later</i>
                      </span>
                    </li>

                    <!-- declined -->
                    <li class="collection-item red lighten-4" v-if="claim.authorization == 'z' && claim.status == 'd'">
                      <s>
                        <span class="tooltipped pointable" data-position="top" :data-tooltip="claim.comment"
                          v-if="claim.comment">
                          <b>{{ claim.item }}</b> x
                          {{ claim.sent_qty }} (Charged: {{
                            toMoney(claim.sent_price)
                          }})
                        </span>

                        <span v-else>
                          <b>{{ claim.item }}</b> x
                          {{ claim.sent_qty }} (Charged: {{
                            toMoney(claim.sent_price)
                          }})
                        </span>
                      </s>

                      <span class="secondary-content red-text pointable" title="Declined"><i
                          class="material-icons">cancel</i></span>
                    </li>
                  </span>
                </ol>
              </div>

            </div>
          </template>

        </div>
      </div>
    </div>

    <div class="section center" v-if="!encounter">
      <h6>No Encounter Record for Enrollee <em>found</em>...</h6>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUpdated, watchEffect, computed } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { toMoneyFx } from "@/composables/calFunction/cal.js";
import { ageSex } from "@/composables/fxLibrary/coreFx";
import postItem from "@/composables/postItem";
import LoadingDiv from "@/components/LoadingDiv.vue";
import AddClaim from "@/components/claim/AddClaimComponent.vue";
import { getMinDate, getMaxDate } from "@/composables/encounter/eligible";
import { checkEnrolleeHistory } from "@/composables/serverCaller/singleEncounterHistory";
import Modal from "@/components/alerts/ModalComponent.vue";
import M from "materialize-css";


export default {
  name: "EnrolleeEncounterComponent",
  props: ["enrollee", "serverDate", "searchPeriod"],
  emits: ["restart", "refresh"],
  components: { LoadingDiv, AddClaim, Modal },
  setup(props, context) {
    // settings
    const SETTINGS_VISIT = props.enrollee.scheme_id === 1 ? true : false;
    // others
    const response = ref(null);
    const selectedItem = ref(null);
    const isReady = ref(true);
    const isLoading = ref(false);
    const isSubmitting = ref(false);
    const alertTitle = ref("Confirm");
    const alertMessage = ref("Are you sure? Please confirm that all the details provided are accurate. Once submitted, changes cannot be made.");
    const error = ref(false);
    const encounterDate = ref(null);
    const encounterRegister = ref(null);
    const encounterHistory = ref(null);
    const editId = ref(null);
    const editClaim = ref(null);
    const deleteClaim = ref(null);
    const claimsDisplayCard = ref(false);
    const claimsSubmitCard = ref(false);
    const diagnosisList = ref([]);
    const resetDiagnosis = ref(false);
    const type = ref("auth"); // close claim after input
    const next = ref("next");
    const claimsList = ref([]);
    const claim = ref(null);
    const reload = ref(null);


    // vue router
    const router = useRouter();

    // store
    const store = useStore();

    // user id
    const userId = store.state.user?.id;

    // user level
    const level = store.state.user?.level;

    // config
    const providerId = store.state?.user?.account?.id;
    
    // toolTip
    const toolTip = () => {
      var elems = document.querySelectorAll(".tooltipped");
      M.Tooltip.init(elems, {});
    };

    // restart toolTip
    const restartComments = () => {
      // load comment
      setTimeout(() => {
        toolTip();
      }, 2000);
    };

    // load after data loads and updates
    watchEffect(() => {
      if (response.value) {
        restartComments();
      }
    });

    // add up
    const totalSent = (claims) => {
      let total = 0;
      claims.forEach((x) => {
        total = Number(total) + Number(x.amt);
      });
      return total
        .toFixed(2)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    };

    // to Money
    const toMoney = (val) => {
      return toMoneyFx(val);
    }

    // ageSex
    const showAgeSex = (dob, sex) => {
      const { fx } = ageSex(dob, sex);
      return fx.value;
    };

    // scrollbar
    const resetScrollBar = () => {
      // Restore scrollbar (insurance)
      document.body.style.overflow = 'auto';
    };

    // loader
    const encounterStarter = (val) => {
      // get started
      response.value = val;
      // set provider immediately
      response.value.provider_id = providerId;
      // check if it's a reload
      if ('diagnosis' in val) {
        // reload diagnosis
        reload.value = response.value?.diagnosis;
      }
      if ('encounter_date' in val) {
        // reload encounter date
        encounterDate.value = response.value?.encounter_date;
        // pass register lock
        encounterRegister.value = encounterDate.value;
      }
    }

    // starter
    onMounted(() => {
      // start
      encounterStarter(props.enrollee);
    });

    // alert
    onMounted(() => {
      // modal
      const elems = document.querySelectorAll(".modal");
      M.Modal.init(elems, {});

      // only when dtepicker is required
      if (!props.serverDate || !props.searchPeriod) return;

      // activate date
      const elemsDate = document.querySelectorAll(".datepicker");
      const minDate = getMinDate(props.serverDate, props.searchPeriod, props.enrollee);
      const maxDate = getMaxDate(props.serverDate, props.searchPeriod, props.enrollee);

      M.Datepicker.init(elemsDate, {
        minDate: minDate,
        maxDate: maxDate,
        onSelect: function (date) {

          let newDate = new Date(date);
          if (SETTINGS_VISIT) {
            encounterRegister.value = newDate
              .toLocaleDateString("en-GB")
              .split("/")
              .reverse()
              .join("-");
          } else {
            encounterDate.value = newDate
              .toLocaleDateString("en-GB")
              .split("/")
              .reverse()
              .join("-");
            // ensure next
            encounterRegister.value = encounterDate.value;
          }

        },

      });

    });

    // alert
    onUpdated(() => {
      // modal
      const elems = document.querySelectorAll(".modal");
      M.Modal.init(elems, {});

      // only when dtepicker is required
      if (!props.serverDate || !props.searchPeriod) return;

      // activate date
      const elemsDate = document.querySelectorAll(".datepicker");
      const minDate = getMinDate(props.serverDate, props.searchPeriod, props.enrollee);
      const maxDate = getMaxDate(props.serverDate, props.searchPeriod, props.enrollee);

      M.Datepicker.init(elemsDate, {
        minDate: minDate,
        maxDate: maxDate,
        onSelect: function (date) {

          let newDate = new Date(date);
          if (SETTINGS_VISIT) {
            encounterRegister.value = newDate
              .toLocaleDateString("en-GB")
              .split("/")
              .reverse()
              .join("-");
          } else {
            encounterDate.value = newDate
              .toLocaleDateString("en-GB")
              .split("/")
              .reverse()
              .join("-");
            // ensure next
            encounterRegister.value = encounterDate.value;
          }

        },

      });

    });

    // handle Continue
    const handleEncounterNext = (val) => {
      if (val === "auth") {
        // load encounter Date
        encounterDate.value = encounterRegister.value;
      } else if (val === "save") {
        // call submit
        handleSubmit();
      }
    }

    // watch updates
    watchEffect(() => {
      if (encounterDate.value) {
        // release window
        // insurance
        resetScrollBar();
      }
    });

    // handle date reset
    const handleDateReset = () => {
      // clear
      encounterDate.value = null;
      // clear register
      encounterRegister.value = null;
      // clear loader
      isLoading.value = false;
    }

    // handle Emitted update
    const handleUpdate = (val) => {
      claimsDisplayCard.value = val.claimsDisplayCard;
      claimsSubmitCard.value = val.claimsSubmitCard;
      claimsList.value = [...val.claimsList];
      diagnosisList.value = [...val.diagnosisList];
    };

    // handle edit
    const handleEdit = (val) => {
      claim.value = { action: "edit", claim: val };
      editId.value = val.id;
      // top
      router.push({ hash: "#edit" });
    };

    // handle edit
    const handleDelete = (val) => {
      claim.value = { action: "delete", claim: val };
    };

    // handle remove (passed as props)
    const handleReviewDiagnosis = () => {
      resetDiagnosis.value = true;
    };

    // handle next
    const handleNext = (val) => {
      next.value = val;
    };

    // handle clear (emiited)
    const handleClear = (val) => {
      if (val.id == "resetDiagnosis") {
        resetDiagnosis.value = val.value;
      } else if (val.id == "claim") {
        claim.value = val.value;
      } else if (val.id == "next") {
        next.value = val.value;
      } else if (val.id == "editId") {
        editId.value = val.value;
      }
      // top
      router.push({ hash: "#claim" });
    };

    // hanlde restart
    const handleRestart = () => {
      context.emit("restart", null);
    };

    // confirmation
    const handleConfirm = (val) => {
      // call submit if confirmed
      if (val) {
        handleSubmit();
      }
    }

    // handle final submit
    const handleUpdateSubmit = async () => {

      // start
      isSubmitting.value = true;

      // provier
      response.value.provider_id = providerId;
      // add encounter date
      response.value.encounter_date = encounterDate.value; // (goes into encounter)

      // setup encounter
      let diagnosis = null;

      if (encounter.value.diagnosis === diagnosisList.value.join(", ")) {
        diagnosis = encounter.value.diagnosis;
      } else {
        if (encounter.value.diagnosis) {
          diagnosis = `(${encounter.value.diagnosis}) ${diagnosisList.value.join(", ")}`;
        } else {
          diagnosis = diagnosisList.value.join(", ");
        }
      }

      let keysToRemove = ["scheme", "created_at", "updated_at"];
      let newEncounter = {
        ...Object.fromEntries(
          Object.entries(encounter.value).filter(
            ([key]) => !keysToRemove.includes(key)
          )
        ),
      };
      newEncounter = {
        ...newEncounter,
        diagnosis: diagnosis,
        user_id: userId,
      };

      // setup claims
      let newClaims = [];

      // auth checker
      let authorization_status;
      let authorization_code;

      // load  
      claims.value.forEach((x) => {
        if (x.amt == 0) {
          authorization_status = "y";
          authorization_code = "Capitation";
        } else {
          authorization_status = "z";
          authorization_code = null;
        }

        newClaims.push({
          encounter_id: null,
          item: x.claim,
          sent_qty: x.qty,
          sent_price: x.amt,
          approved_qty: x.qty,
          approved_price: x.amt,
          standard_price: x.standard,
          authorization: authorization_status,
          authorization_code: authorization_code,
          user_id: userId,
        });
      });

      // put all together
      let newVal = { encounter: newEncounter, claims: newClaims };

      // post item
      const { res, err } = await postItem("claim/create/bulk", newVal);

      if (res.value) {

        if (res.value.status) {

          // final end
          handleRestart();

          // reset on fail
          resetScrollBar();


        } else {
          // claims month has closed
          error.value = "Bills for enrollee Month has been submitted!";
          isSubmitting.value = false; // end

          // reset on fail
          resetScrollBar();
        }


      } else {

        // manage error
        error.value = err.value;
        isSubmitting.value = false; // end

        // reset on fail
        resetScrollBar();

      }
    };

    // handle final submit
    const handleSubmit = async () => {

      // main check
      if (encounter.value?.encounter_date_set) {

        //update here
        handleUpdateSubmit();

      } else {

        // real submit
        // start
        isSubmitting.value = true;

        // provier
        response.value.provider_id = providerId;
        // add encounter date
        response.value.encounter_date = encounterDate.value ? encounterDate.value : encounterRegister.value; // (goes into encounter)

        // setup encounter
        let diagnosis = diagnosisList.value.join(", ");
        let keysToRemove = ["id", "encounter", "plan", "scheme", "created_at", "updated_at"];
        let newEncounter = {
          ...Object.fromEntries(
            Object.entries(encounter.value).filter(
              ([key]) => !keysToRemove.includes(key)
            )
          ),
        };
        newEncounter = {
          ...newEncounter,
          encounter_cluster_id: null,
          diagnosis: diagnosis,
          user_id: userId,
        };

        // setup claims
        let newClaims = [];

        // auth checker
        let authorization_status;
        let authorization_code;

        // load
        claims.value.forEach((x) => {
          if (x.amt == 0) {
            authorization_status = "y";
            authorization_code = "Capitation";
          } else {
            authorization_status = "z";
            authorization_code = null;
          }

          newClaims.push({
            encounter_id: null,
            item: x.claim,
            sent_qty: x.qty,
            sent_price: x.amt,
            approved_qty: x.qty,
            approved_price: x.amt,
            standard_price: x.standard,
            authorization: authorization_status,
            authorization_code: authorization_code,
            user_id: userId,
          });
        });

        // put all together
        let newVal = { encounter: newEncounter, claims: newClaims };

        // post item
        const { res, err } = await postItem("claim/encounter/bulk", newVal);

        if (res.value) {


          if (res.value.status) {

            // final end
            handleRestart();

            // reset on fail
            resetScrollBar();

          } else {
            // claims month has closed
            error.value = "Bills for enrollee Month has been submitted!";
            isSubmitting.value = false; // end

            // reset on fail
            resetScrollBar();
          }

        } else {

          // manage errors
          error.value = err.value;
          isSubmitting.value = false; // end

          // reset on fail
          resetScrollBar();

        }

      }
    };

    // // watch encounter selection
    watchEffect(() => {
      if (encounterRegister.value && !('encounter_date' in props.enrollee) && !encounterHistory.value) {
        
        (async () => {
          // start laoding immediately
        isLoading.value = true;
        // check enrollee history
        let historyRes = await checkEnrolleeHistory(encounter.value, encounterRegister.value);
        if (historyRes?.status) {
          if (historyRes?.val) {
            // set encounterHistory
            encounterHistory.value = historyRes.val;
            // restart
            encounterStarter(encounterHistory.value);
          }
        } else {
          // clear
          // it should atleast 
          // return original enrollee detail
          handleRestart();
        }
        // end
        isLoading.value = false;
        })();
      }

    });

    // output encounter results
    const encounter = computed(() => {
      if (!response.value) {
        return null;
      } else {
        return response.value;
      }
    });

    // output claims results
    const claims = computed(() => {
      if (!claimsList.value) {
        return [];
      } else {
        return claimsList.value;
      }
    });

    return {
      encounterRegister,
      level,
      toMoney,
      showAgeSex,
      handleEncounterNext,
      handleDateReset,
      handleEdit,
      handleReviewDiagnosis,
      handleDelete,
      handleUpdate,
      handleNext,
      handleRestart,
      handleConfirm,
      handleSubmit,
      handleClear,
      alertTitle,
      alertMessage,
      selectedItem,
      totalSent,
      isReady,
      isLoading,
      isSubmitting,
      error,
      encounterDate,
      editId,
      claimsDisplayCard,
      claimsSubmitCard,
      resetDiagnosis,
      diagnosisList,
      reload,
      editClaim,
      deleteClaim,
      encounter,
      claimsList,
      next,
      type,
      claim,
      claims,
    };
  },
};
</script>

<style scoped>
.progress {
  margin-top: -15px;
}

.spacer5 {
  padding-top: 5px;
  padding-bottom: 5px;
}

.spacerTop10 {
  padding-top: 10px;
  padding-bottom: 0px;
}

.edit-black-item:hover .material-icons.black-text,
.edit-black-item:hover .material-icons.grey-text::before {
  color: rgb(21, 21, 206) !important;
}

.edit-item:hover .material-icons.grey-text,
.edit-item:hover .material-icons.grey-text::before {
  color: rgb(21, 21, 206) !important;
}

.cancel-item:hover .material-icons.grey-text,
.cancel-item:hover .material-icons.grey-text::before {
  color: rgb(214, 16, 16) !important;
}
</style>